import { useCallback, useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  AlertCircle,
  Banknote,
  FileText,
  Landmark,
  Plus,
  ShieldCheck,
  Undo2,
  X,
} from "lucide-react";

import { Spinner } from "../../../components/ui/Spinner";
import Button from "../../../components/ui/Button";
import {
  canRecordPayment,
  coerceAccessState,
  coerceDunningStage,
  coerceInvoiceStatus,
  coerceSubscriptionState,
  formatMoney,
  getPlatformBillingDetail,
  isCustomerManaged,
  type PlatformBillingDetailView,
  type PlatformBillingInvoiceRow,
  type PlatformBillingRowView,
} from "../../../api/General/PlatformBilling";
import { formatDate } from "./helpers";
import {
  dunningStageChip,
  dunningStageLabel,
  invoiceKindLabel,
  operatorActionLabel,
  invoiceLabel,
  invoiceStatusChip,
  invoiceStatusLabel,
  taxTreatmentLabel,
} from "./billingLabels";
import {
  CorrectInvoiceDialog,
  IssueDraftDialog,
  RaiseInvoiceDialog,
  RecordPaymentDialog,
  TaxCountryDialog,
} from "./PlatformInvoiceDialogs";

/*
 * CO-126 — THE ANCHOR. One place per company, instead of a row menu that had grown to eleven items.
 *
 * The information architecture decision this file embodies, so a later pass does not undo it:
 *
 * • THE LIST STAYS A LIST. Platform Billing answers "who needs attention"; this sheet answers
 *   "what is going on with THIS tenant". Every company-scoped surface added from here on belongs
 *   inside this sheet, not as another tab. Three overlapping operator screens already cost eight
 *   runs to map; a fourth would be the same mistake with a new name.
 * • ORDER IS TRIAGE ORDER, not entity order. Who manages it, then what is wrong, then money, then
 *   the documents, then the tax treatment, then history. An operator on a support call reads top to
 *   bottom and stops as soon as the question is answered.
 * • NO STAT CARDS. The money block is a definition list. Four numbers in big type is the
 *   hero-metric template and it teaches operators to stop reading the row that matters.
 * • THE ROW MENU SURVIVES as the fast path for someone who already knows which lever they want. It
 *   is a shortcut into this sheet's vocabulary, not a competing surface.
 */

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: PlatformBillingRowView | null;
  /** Bumped by the parent after any mutation elsewhere, so the sheet re-reads without remounting. */
  refreshToken?: number;
  /** The sheet's own mutations must also refresh the list behind it. */
  onMutated: () => void;
}

function SectionHeading({
  title,
  action,
}: {
  title: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 mb-2">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {action}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium text-foreground tabular-nums text-end">{value}</dd>
    </div>
  );
}

export default function CompanyBillingSheet({
  open,
  onOpenChange,
  row,
  refreshToken = 0,
  onMutated,
}: Props) {
  const { t } = useTranslation();

  const [detail, setDetail] = useState<PlatformBillingDetailView | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [raiseOpen, setRaiseOpen] = useState(false);
  const [taxOpen, setTaxOpen] = useState(false);
  const [payInvoice, setPayInvoice] = useState<PlatformBillingInvoiceRow | null>(null);
  const [issueInvoice, setIssueInvoice] = useState<PlatformBillingInvoiceRow | null>(null);
  const [correctInvoice, setCorrectInvoice] = useState<PlatformBillingInvoiceRow | null>(null);

  const companyId = row?.companyId ?? null;
  const companyName = row?.companyName ?? "";

  /*
   * CO-120's lesson, applied here from the start rather than after an E2E failure taught it: two
   * loads can land out of order (open company A, close, open company B, A's slower response wins)
   * and the sheet would then show ANOTHER TENANT'S money under this one's name. That is worse than
   * a stale list, so the guard is not optional on a per-company view.
   */
  const reqIdRef = useRef(0);
  const load = useCallback(async () => {
    if (!companyId) return;
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setErrorMsg(null);
    try {
      const result = await getPlatformBillingDetail(companyId);
      if (reqId !== reqIdRef.current) return;
      const msg = result.errors?.[0]?.description;
      if (msg) {
        setErrorMsg(msg);
        setDetail(null);
        return;
      }
      setDetail(result.payload ?? null);
    } catch {
      if (reqId !== reqIdRef.current) return;
      setErrorMsg(t("Could not load this company's billing detail."));
      setDetail(null);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [companyId, t]);

  useEffect(() => {
    if (open) {
      void load();
      return;
    }
    /*
     * 🔴 INVALIDATING ON CLOSE IS THE LOAD-BEARING HALF, NOT `setDetail(null)`.
     *
     * Comparing `reqId` is not enough on its own. Open company A (request 1 in flight) → close →
     * A's response lands and STILL matches `reqIdRef.current`, so it writes A's detail back into
     * state after the clear → open company B. `detail` is now non-null, so the
     * `loading && !detail` spinner never covers the gap, and B's header renders over **A's invoice
     * register, A's tax profile and A's operator-action history** until B's fetch resolves. In that
     * window `TaxCountryDialog` is also handed A's `taxProfile` as the "today" side of the panel.
     *
     * Mutations cannot actually cross tenants (every server lookup is company-scoped), so this is a
     * disclosure and PII bug rather than a data-integrity one — which makes it exactly the kind that
     * survives testing. Bumping the counter makes the late response un-matchable.
     */
    reqIdRef.current++;
    setDetail(null);
    // A's error banner must not greet B either.
    setErrorMsg(null);
    setLoading(false);
  }, [open, load, refreshToken]);

  const afterMutation = useCallback(() => {
    void load();
    onMutated();
  }, [load, onMutated]);

  const customerManaged = row ? isCustomerManaged(row) : true;
  const currency = row?.currency ?? detail?.summary.currency ?? "AED";
  const invoices = detail?.recentInvoices ?? [];

  /*
   * CO-144. `null` here means NOT IN DUNNING — the common case — and it must never be coerced into a
   * stage. The chip and every Fact below live behind this check for that reason.
   */
  const ladder = detail?.dunningLadder ?? null;
  const ladderStage = coerceDunningStage(ladder?.stage);

  return (
    <>
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            className="fixed top-0 z-50 h-full w-full max-w-xl
              ltr:right-0 rtl:left-0
              bg-card border-border ltr:border-l rtl:border-r shadow-2xl flex flex-col
              data-[state=open]:animate-in data-[state=open]:ltr:slide-in-from-right data-[state=open]:rtl:slide-in-from-left
              data-[state=closed]:animate-out data-[state=closed]:ltr:slide-out-to-right data-[state=closed]:rtl:slide-out-to-left
              data-[state=open]:duration-300 data-[state=closed]:duration-200"
          >
            {/* ── Header: the triage answer, before anything else ── */}
            <div className="p-5 border-b border-border">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <DialogPrimitive.Title className="text-base font-semibold text-foreground truncate">
                    {companyName}
                  </DialogPrimitive.Title>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {customerManaged
                      ? t("The customer manages this subscription.")
                      : t("Mowazaf manages this subscription. The customer's portal is read-only.")}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                    <span>{t(coerceSubscriptionState(row?.state) ?? "Unknown")}</span>
                    <span className="text-muted-foreground/50">·</span>
                    <span>{t(coerceAccessState(row?.accessState) ?? "Unknown")}</span>
                    {row?.planCode && (
                      <>
                        <span className="text-muted-foreground/50">·</span>
                        <span>{row.planCode}</span>
                      </>
                    )}
                  </div>
                </div>
                <DialogPrimitive.Close
                  aria-label={t("Close")}
                  className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                >
                  <X size={18} aria-hidden="true" />
                </DialogPrimitive.Close>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading && !detail ? (
                <div className="flex items-center justify-center py-20">
                  <Spinner />
                </div>
              ) : errorMsg ? (
                <div className="m-5 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                  <span>{errorMsg}</span>
                </div>
              ) : !detail ? null : (
                <div className="divide-y divide-border">
                  {/* ── What is wrong, if anything. Only rendered when there is something to read. ── */}
                  {detail.divergenceSummary && (
                    <div className="px-5 py-4">
                      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
                        <ShieldCheck size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                        <span>{detail.divergenceSummary}</span>
                      </div>
                    </div>
                  )}

                  {/*
                    ── CO-144 — WHERE ON THE LADDER, AND WHEN IS THE NEXT ATTEMPT. ──

                    Placed ABOVE the money, and that is the whole point of the section: outstanding
                    tells an operator the tenant owes something, the ladder tells them what the tenant
                    has already LOST and what they are about to lose next. On a support call the second
                    question is asked first, and until this shipped the product could not answer it
                    anywhere — not here, not in the platform view models, not in the diagnostics.

                    Rendered in the sheet's existing definition-list vocabulary rather than as a
                    progress tracker: a stepper would imply the ladder advances on a fixed clock the
                    operator can watch, when the truth is a retry schedule with a next-attempt stamp,
                    which is exactly what the rows below say.
                  */}
                  <section className="px-5 py-4">
                    <SectionHeading title={t("Dunning ladder")} />
                    {ladder ? (
                      <>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={dunningStageChip(ladderStage)}>
                            {t(dunningStageLabel(ladderStage))}
                          </span>
                          {ladder.openLadderCount > 1 && (
                            <span className="text-xs text-muted-foreground">
                              {/*
                                `n`, not `count` — i18next treats a `count` option as a plural
                                selector and would look for `_one` / `_other` variants that do not
                                exist, which resolves to the raw key on screen.
                              */}
                              {t("{{n}} unpaid invoices are being chased", {
                                n: ladder.openLadderCount,
                              })}
                            </span>
                          )}
                        </div>
                        <dl className="mt-2 divide-y divide-border">
                          <Fact
                            label={t("On this step since")}
                            value={formatDate(ladder.stageEnteredAtUtc)}
                          />
                          <Fact
                            label={t("Next attempt")}
                            value={
                              ladder.nextAttemptAtUtc ? (
                                formatDate(ladder.nextAttemptAtUtc)
                              ) : (
                                /*
                                  A null next attempt is NOT "unknown" — the retry schedule has run
                                  out, so nothing will try again on its own. Rendering a dash here
                                  would let an operator wait for a payment attempt that is never
                                  coming.
                                */
                                <span className="text-muted-foreground">
                                  {t("Nothing further is scheduled")}
                                </span>
                              )
                            }
                          />
                          <Fact label={t("Attempts made")} value={ladder.attemptsMade} />
                          <Fact
                            label={t("First failed")}
                            value={formatDate(ladder.firstFailureAtUtc)}
                          />
                          <Fact
                            label={t("Unpaid invoice")}
                            value={ladder.invoiceNumber ?? t("Not yet numbered")}
                          />
                          {ladder.lastFailureCode && (
                            <Fact label={t("Last failure")} value={ladder.lastFailureCode} />
                          )}
                        </dl>
                      </>
                    ) : (
                      /*
                        🔴 The empty state is a REAL ANSWER, not a placeholder. `dunningLadder` is null
                        for nearly every tenant, and the failure this section exists to prevent is
                        showing a default stage for a healthy customer — so it says "not in dunning" in
                        words rather than rendering a step of the ladder they are not on.
                      */
                      <p className="text-sm text-muted-foreground">
                        {t(
                          "This tenant is not in dunning. No payment has failed, so nothing is being retried and no access step is scheduled.",
                        )}
                      </p>
                    )}
                  </section>

                  {/* ── Money. A definition list, deliberately not stat cards. ── */}
                  <section className="px-5 py-4">
                    <SectionHeading title={t("Where the money stands")} />
                    <dl className="divide-y divide-border">
                      <Fact
                        label={t("Outstanding")}
                        value={
                          <span
                            className={
                              (row?.outstandingGross ?? 0) > 0
                                ? "text-amber-700 dark:text-amber-400"
                                : undefined
                            }
                          >
                            {formatMoney(row?.outstandingGross, currency)}
                          </span>
                        }
                      />
                      <Fact
                        label={t("Account credit")}
                        value={formatMoney(detail.creditBalance, currency)}
                      />
                      <Fact
                        label={t("Of which is refundable cash")}
                        value={formatMoney(detail.cashOriginCreditBalance, currency)}
                      />
                      <Fact
                        label={t("Rate per seat, per month")}
                        value={formatMoney(row?.effectiveRatePerSeatPerMonth, currency)}
                      />
                      <Fact
                        label={t("Term ends")}
                        value={row?.neverExpires ? t("Never") : formatDate(row?.termEndUtc)}
                      />
                    </dl>
                  </section>

                  {/* ── The register. This is CO-122. ── */}
                  <section className="px-5 py-4">
                    <SectionHeading
                      title={t("Invoices")}
                      action={
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRaiseOpen(true)}
                          disabled={!companyId}
                        >
                          <Plus size={14} aria-hidden="true" className="me-1.5" />
                          {t("Raise an invoice")}
                        </Button>
                      }
                    />

                    {invoices.length === 0 ? (
                      <div className="rounded-md border border-dashed border-border px-4 py-8 text-center">
                        <p className="text-sm font-medium text-foreground">
                          {t("No invoices yet")}
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {customerManaged
                            ? t(
                                "The billing engine raises these automatically at checkout and each renewal.",
                              )
                            : t(
                                "This tenant is invoiced by hand. Raise one here, then record the payment when the transfer arrives.",
                              )}
                        </p>
                      </div>
                    ) : (
                      <ul className="divide-y divide-border rounded-md border border-border">
                        {invoices.map((inv) => {
                          const settleable = canRecordPayment(inv, customerManaged);
                          const isDraft = coerceInvoiceStatus(inv.status) === "Draft";
                          const correctable =
                            !isDraft && coerceInvoiceStatus(inv.status) !== "Void";

                          return (
                            <li key={inv.id} className="px-3 py-3">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-sm font-medium text-foreground">
                                      {invoiceLabel(inv, t)}
                                    </span>
                                    <span className={invoiceStatusChip(inv.status)}>
                                      {t(invoiceStatusLabel(inv.status))}
                                    </span>
                                  </div>
                                  <p className="mt-0.5 text-xs text-muted-foreground">
                                    {t(invoiceKindLabel(inv.kind))}
                                    {inv.issuedAtUtc && (
                                      <>
                                        <span className="mx-1.5 text-muted-foreground/50">·</span>
                                        {t("issued {{date}}", { date: formatDate(inv.issuedAtUtc) })}
                                      </>
                                    )}
                                    {inv.dueAtUtc && (
                                      <>
                                        <span className="mx-1.5 text-muted-foreground/50">·</span>
                                        {t("due {{date}}", { date: formatDate(inv.dueAtUtc) })}
                                      </>
                                    )}
                                  </p>
                                </div>

                                <div className="shrink-0 text-end tabular-nums">
                                  <div className="text-sm font-medium text-foreground">
                                    {formatMoney(inv.totalGross, inv.currency)}
                                  </div>
                                  {inv.amountDue > 0 && (
                                    <div className="text-xs text-amber-700 dark:text-amber-400">
                                      {t("{{amount}} owing", {
                                        amount: formatMoney(inv.amountDue, inv.currency),
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {(settleable || isDraft || correctable) && (
                                <div className="mt-2 flex flex-wrap items-center gap-3">
                                  {settleable && (
                                    <button
                                      type="button"
                                      onClick={() => setPayInvoice(inv)}
                                      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                                    >
                                      <Banknote size={14} aria-hidden="true" />
                                      {t("Record a payment")}
                                    </button>
                                  )}
                                  {isDraft && (
                                    <button
                                      type="button"
                                      onClick={() => setIssueInvoice(inv)}
                                      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                                    >
                                      <FileText size={14} aria-hidden="true" />
                                      {t("Issue it")}
                                    </button>
                                  )}
                                  {correctable && (
                                    <button
                                      type="button"
                                      onClick={() => setCorrectInvoice(inv)}
                                      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
                                    >
                                      <Undo2 size={14} aria-hidden="true" />
                                      {t("Credit back")}
                                    </button>
                                  )}
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}

                    {/*
                      Said once, on the register rather than inside the dialog, because the question
                      "why can I not settle this one?" is asked while LOOKING AT THE LIST.
                    */}
                    {invoices.length > 0 && !invoices.some((i) => canRecordPayment(i, customerManaged)) && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        {customerManaged
                          ? t(
                              "Payments are recorded automatically for this tenant. Recording one by hand is only for invoices Mowazaf raised itself.",
                            )
                          : t("Nothing here is waiting on a hand-collected payment.")}
                      </p>
                    )}
                  </section>

                  {/* ── Tax treatment. This is CO-125. ── */}
                  <section className="px-5 py-4">
                    <SectionHeading
                      title={t("Tax treatment")}
                      action={
                        <Button variant="outline" size="sm" onClick={() => setTaxOpen(true)}>
                          <Landmark size={14} aria-hidden="true" className="me-1.5" />
                          {detail.taxProfile ? t("Change") : t("Why not?")}
                        </Button>
                      }
                    />
                    {detail.taxProfile ? (
                      <dl className="divide-y divide-border">
                        <Fact label={t("Taxed in")} value={detail.taxProfile.taxCountry} />
                        <Fact
                          label={t("Treatment")}
                          value={t(taxTreatmentLabel(String(detail.taxProfile.resolvedTaxTreatment)))}
                        />
                        <Fact
                          label={t("Rate")}
                          value={`${detail.taxProfile.resolvedTaxRatePercent}%`}
                        />
                        {detail.taxProfile.taxRegistrationNumber && (
                          <Fact
                            label={t("Registration number")}
                            value={detail.taxProfile.taxRegistrationNumber}
                          />
                        )}
                      </dl>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {t(
                          "No billing profile yet, so no tax treatment has been resolved. It is created on the tenant's first checkout or when they save their billing details.",
                        )}
                      </p>
                    )}
                  </section>

                  {/* ── History. Quiet, last, and read-only. ── */}
                  {detail.overrideHistory.length > 0 && (
                    <section className="px-5 py-4">
                      <SectionHeading title={t("Operator actions")} />
                      <ol className="space-y-2.5">
                        {detail.overrideHistory.slice(0, 12).map((entry) => (
                          <li key={entry.id} className="text-sm">
                            <div className="flex items-baseline justify-between gap-3">
                              <span className="font-medium text-foreground">
                                {t(operatorActionLabel(entry.kind))}
                              </span>
                              <span className="shrink-0 text-xs text-muted-foreground">
                                {formatDate(entry.performedAt)}
                              </span>
                            </div>
                            {entry.reason && (
                              <p className="text-muted-foreground max-w-[60ch]">{entry.reason}</p>
                            )}
                            {entry.performedByName && (
                              <p className="text-xs text-muted-foreground">{entry.performedByName}</p>
                            )}
                          </li>
                        ))}
                      </ol>
                    </section>
                  )}
                </div>
              )}
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      {/*
        Rendered OUTSIDE the sheet's Radix root. Nesting a Dialog inside an open Dialog makes the
        inner one's Escape and outside-click close the OUTER one too, which would dismiss the whole
        sheet behind a half-filled payment form.
      */}
      <RaiseInvoiceDialog
        open={raiseOpen}
        onOpenChange={setRaiseOpen}
        companyId={companyId}
        companyName={companyName}
        currency={currency}
        onSuccess={() => {
          toast.success(t("Invoice raised."));
          afterMutation();
        }}
      />
      <RecordPaymentDialog
        open={payInvoice !== null}
        onOpenChange={(o) => !o && setPayInvoice(null)}
        companyId={companyId}
        companyName={companyName}
        invoice={payInvoice}
        onSuccess={() => {
          toast.success(t("Payment recorded. The invoice is settled in full."));
          afterMutation();
        }}
      />
      <IssueDraftDialog
        open={issueInvoice !== null}
        onOpenChange={(o) => !o && setIssueInvoice(null)}
        companyId={companyId}
        companyName={companyName}
        invoice={issueInvoice}
        onSuccess={() => {
          toast.success(t("Invoice issued and numbered."));
          afterMutation();
        }}
      />
      <CorrectInvoiceDialog
        open={correctInvoice !== null}
        onOpenChange={(o) => !o && setCorrectInvoice(null)}
        companyId={companyId}
        companyName={companyName}
        invoice={correctInvoice}
        onSuccess={() => {
          toast.success(t("Credit note raised against the invoice."));
          afterMutation();
        }}
      />
      <TaxCountryDialog
        open={taxOpen}
        onOpenChange={setTaxOpen}
        companyId={companyId}
        companyName={companyName}
        taxProfile={detail?.taxProfile ?? null}
        onSuccess={() => {
          toast.success(t("Tax country changed. Future invoices only."));
          afterMutation();
        }}
      />
    </>
  );
}
